import { addAdminMember, adminQueries, removeAdminMember, setAdminMemberAdmin } from "@jkast90/kast-common/admin-core";
import { hasGroup } from "@jkast90/kast-common/auth";
import { SLOW_CALL, apiRequest } from "@common/api";
import { ADMIN_GROUPS, session } from "../auth/session";

// Client for play-api's admin routes; the API refuses anyone outside the
// admin group, this only decides whether to show the page.

export interface SaveFile {
  name: string;
  size: number;
  updatedAt: string;
}

/** The signed-in account's sub and whether its token carries the admin group. */
export function signedInAdmin(): { sub: string | null; admin: boolean } {
  const { user } = session.getState();
  return { sub: user?.sub || null, admin: hasGroup(user, ADMIN_GROUPS) };
}

const json = (body: unknown): RequestInit => ({
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

/** "cover" saved, "miss" (libretro has none) or "skip" (no console with art, or no game file inside). */
export interface CoverSync {
  outcome: "cover" | "miss" | "skip";
  system: string | null;
  title: string | null;
}

/** One of CloudBoy's log lines: the API's own, or one of its requests. */
export interface LogLine {
  at: string;
  level: "error" | "warn" | "info";
  text: string;
}

export interface LogQuery {
  source: "api" | "requests";
  minutes: number;
  problems: boolean;
  q: string;
}

export const admin = {
  /** Newest first, at most 300. */
  logs: async ({ source, minutes, problems, q }: LogQuery): Promise<LogLine[]> =>
    (await apiRequest(`/admin/logs?${new URLSearchParams({ source, minutes: String(minutes), problems: String(problems), q })}`, {}, SLOW_CALL)).json(),
  addMember: (email: string) => addAdminMember(apiRequest, "", email),
  removeMember: (sub: string) => removeAdminMember(apiRequest, "", sub),
  setAdmin: (sub: string, isAdmin: boolean) => setAdminMemberAdmin(apiRequest, "", sub, isAdmin),
  saves: async (sub: string): Promise<SaveFile[]> => (await apiRequest(`/admin/saves/${encodeURIComponent(sub)}`)).json(),
  deleteSave: async (sub: string, name: string) => {
    await apiRequest(`/admin/saves/${encodeURIComponent(sub)}/${encodeURIComponent(name)}`, { method: "DELETE" });
  },
  /** How many saves and players' play times moved with it, and whether its cover did. */
  renameRom: async (id: string, name: string): Promise<{ saves: number; cover: boolean; plays: number }> =>
    (await apiRequest(`/admin/library/${encodeURIComponent(id)}/rename`, { method: "POST", ...json({ name }) }, SLOW_CALL)).json(),
  /** Finds one library game's box art and saves it as its cover. */
  syncCover: async (id: string): Promise<CoverSync> =>
    (await apiRequest(`/admin/covers/${encodeURIComponent(id)}`, { method: "POST" }, SLOW_CALL)).json(),
  deleteRom: async (id: string) => {
    await apiRequest(`/library/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

/** The deploys, lambdas, visitors (most recent first, at most 1000) and members, keyed under ["admin"]. */
export const adminQuery = adminQueries(apiRequest);
